/**
 * MafiaNightAction — Role-specific night action panels
 *
 * Mafia: target list with shared votes
 * Doctor: protection with consecutive restriction
 * Detective: investigation with result
 * Villager: waiting screen
 *
 * Requirements: 4.1–4.3, 5.1–5.4, 6.1–6.3, 21.1, 21.2
 */

import React from 'react';
import { MafiaRole, MafiaPlayer, InvestigationResult } from '../types';
import './MafiaNightAction.css';

export interface MafiaNightActionProps {
  role: MafiaRole;
  alivePlayers: MafiaPlayer[];
  mafiaVotes: Map<string, string>;
  lastProtectedPlayerId?: string;
  onMafiaVote: (targetId: string) => void;
  onDoctorProtect: (targetId: string) => void;
  onDetectiveInvestigate: (targetId: string) => void;
  investigationResult?: InvestigationResult;
  disabled: boolean;
  myPlayerId?: string;
  mySelection?: string;
}

export const MafiaNightAction: React.FC<MafiaNightActionProps> = ({
  role,
  alivePlayers,
  mafiaVotes,
  lastProtectedPlayerId,
  onMafiaVote,
  onDoctorProtect,
  onDetectiveInvestigate,
  investigationResult,
  disabled,
  myPlayerId,
  mySelection,
}) => {
  // Mafia panel
  if (role === 'mafia') {
    const targets = alivePlayers.filter((p) => p.role !== 'mafia');
    // Tally mafia votes
    const voteTally = new Map<string, number>();
    for (const targetId of Array.from(mafiaVotes.values())) {
      voteTally.set(targetId, (voteTally.get(targetId) || 0) + 1);
    }
    const aliveMafiaCount = alivePlayers.filter((p) => p.role === 'mafia').length;
    const majorityNeeded = Math.floor(aliveMafiaCount / 2) + 1;

    return (
      <div className="mafia-night-action" role="region" aria-label="Mafia Night Action">
        <h3 className="mafia-night-action__title">🔪 Choose a Target</h3>
        <ul className="mafia-night-action__list">
          {targets.map((player) => {
            const votes = voteTally.get(player.id) || 0;
            const isSelected = mySelection === player.id;
            const hasMajority = votes >= majorityNeeded;

            return (
              <li key={player.id}>
                <button
                  className={`mafia-night-action__target ${isSelected ? 'mafia-night-action__target--selected' : ''} ${disabled ? 'mafia-night-action__target--disabled' : ''}`}
                  onClick={() => !disabled && onMafiaVote(player.id)}
                  disabled={disabled}
                  aria-label={`Target ${player.name} (${votes} votes)`}
                >
                  <span className="mafia-night-action__target-name">{player.name}</span>
                  {votes > 0 && (
                    <span className={`mafia-night-action__target-badge ${hasMajority ? 'mafia-night-action__target-badge--majority' : ''}`}>
                      {votes} vote{votes !== 1 ? 's' : ''}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  // Doctor panel
  if (role === 'doctor') {
    return (
      <div className="mafia-night-action" role="region" aria-label="Doctor Night Action">
        <h3 className="mafia-night-action__title">💊 Choose Who to Protect</h3>
        <ul className="mafia-night-action__list">
          {alivePlayers.map((player) => {
            const isRestricted = player.id === lastProtectedPlayerId;
            const isSelected = mySelection === player.id;

            return (
              <li key={player.id}>
                <button
                  className={`mafia-night-action__target ${isSelected ? 'mafia-night-action__target--selected' : ''} ${isRestricted ? 'mafia-night-action__target--restricted' : ''} ${disabled ? 'mafia-night-action__target--disabled' : ''}`}
                  onClick={() => !disabled && !isRestricted && onDoctorProtect(player.id)}
                  disabled={disabled || isRestricted}
                  aria-label={`Protect ${player.name}${isRestricted ? ' (protected last night)' : ''}`}
                >
                  <span className="mafia-night-action__target-name">
                    {player.name} {player.id === myPlayerId ? '(You)' : ''}
                  </span>
                  {isRestricted && (
                    <span className="mafia-night-action__target-badge">Last night</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
        <div className="mafia-night-action__restricted-note">
          Cannot protect the same player two nights in a row
        </div>
      </div>
    );
  }

  // Detective panel
  if (role === 'detective') {
    const targets = alivePlayers.filter((p) => p.id !== myPlayerId);

    return (
      <div className="mafia-night-action" role="region" aria-label="Detective Night Action">
        <h3 className="mafia-night-action__title">🔍 Choose Who to Investigate</h3>

        {investigationResult ? (
          <div
            className={`mafia-night-action__result ${investigationResult.isMafia ? 'mafia-night-action__result--mafia' : 'mafia-night-action__result--not-mafia'}`}
            role="alert"
          >
            {investigationResult.targetName} is{' '}
            {investigationResult.isMafia ? '🔪 Mafia!' : '✅ Not Mafia'}
          </div>
        ) : (
          <ul className="mafia-night-action__list">
            {targets.map((player) => {
              const isSelected = mySelection === player.id;

              return (
                <li key={player.id}>
                  <button
                    className={`mafia-night-action__target ${isSelected ? 'mafia-night-action__target--selected' : ''} ${disabled ? 'mafia-night-action__target--disabled' : ''}`}
                    onClick={() => !disabled && onDetectiveInvestigate(player.id)}
                    disabled={disabled}
                    aria-label={`Investigate ${player.name}`}
                  >
                    <span className="mafia-night-action__target-name">{player.name}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  // Villager waiting screen
  return (
    <div className="mafia-night-action" role="region" aria-label="Night Phase - Waiting">
      <div className="mafia-night-action__waiting">
        <div className="mafia-night-action__waiting-icon">🌙</div>
        <div className="mafia-night-action__waiting-text">The village sleeps...</div>
      </div>
    </div>
  );
};

export default MafiaNightAction;
